import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../../utils/api';
import { INDIAN_CITIES, COURSE_OPTIONS } from '../../utils/indianCities';

/**
 * Calculate age from date of birth
 */
function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/**
 * Student Form Dialog
 * For adding new students or editing existing ones
 */
function StudentFormDialog({ open, onClose, student, onSuccess, showSnackbar, apiPrefix = '/librarian' }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dateOfBirth: null,
    city: null,
    interested_courses: [],
    password: '',
  });

  const [errors, setErrors] = useState({});

  const isEditMode = !!student;

  useEffect(() => {
    if (student) {
      // Try to estimate date of birth from age if available
      let estimatedDob = null;
      if (student.age) {
        const today = new Date();
        estimatedDob = new Date(today.getFullYear() - student.age, 0, 1);
      }
      // Parse interested_courses - could be comma-separated string or array
      let courses = [];
      if (student.interested_courses) {
        if (Array.isArray(student.interested_courses)) {
          courses = student.interested_courses;
        } else if (typeof student.interested_courses === 'string') {
          courses = student.interested_courses.split(',').map(c => c.trim()).filter(Boolean);
        }
      }
      setFormData({
        name: student.name || '',
        email: student.email || '',
        phone: student.phone || '',
        dateOfBirth: estimatedDob,
        city: student.city || null,
        interested_courses: courses,
        password: '', // Don't populate password in edit mode
      });
    } else {
      // Reset form for add mode
      setFormData({
        name: '',
        email: '',
        phone: '',
        dateOfBirth: null,
        city: null,
        interested_courses: [],
        password: '',
      });
    }
    setErrors({});
  }, [student, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.dateOfBirth) {
      const age = calculateAge(formData.dateOfBirth);
      if (age < 5 || age > 120) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth';
      }
    }

    if (!isEditMode && !formData.password) {
      newErrors.password = 'Password is required for new students';
    } else if (!isEditMode && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        age: formData.dateOfBirth ? calculateAge(formData.dateOfBirth) : null,
        city: formData.city || null,
        interested_courses: formData.interested_courses?.length > 0
          ? formData.interested_courses.join(', ')
          : null,
      };

      // Only include password for new students
      if (!isEditMode && formData.password) {
        payload.password = formData.password;
      }

      let createdStudent = null;

      if (isEditMode) {
        // Update existing student
        await api.put(`${apiPrefix}/students/${student.id}`, payload);
        showSnackbar('Student updated successfully', 'success');
      } else {
        // Create new student
        const response = await api.post(`${apiPrefix}/students`, payload);
        createdStudent = response.data.data;
        showSnackbar('Student created successfully', 'success');
      }

      onSuccess(createdStudent);
      onClose();
    } catch (error) {
      console.error('Error saving student:', error);

      if (error.response?.data?.code === 'EMAIL_EXISTS') {
        setErrors({ email: 'A student with this email already exists' });
        showSnackbar('Email already exists', 'error');
      } else {
        showSnackbar(
          error.response?.data?.message || 'Failed to save student',
          'error'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>{isEditMode ? 'Edit Student' : 'Add New Student'}</span>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Name *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              autoFocus
            />

            <TextField
              label="Email *"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              disabled={isEditMode} // Don't allow changing email in edit mode
            />

            <TextField
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
              fullWidth
            />

            <Box display="flex" gap={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Birth"
                  value={formData.dateOfBirth}
                  onChange={(newValue) => {
                    setFormData({ ...formData, dateOfBirth: newValue });
                    if (errors.dateOfBirth) {
                      setErrors({ ...errors, dateOfBirth: '' });
                    }
                  }}
                  maxDate={new Date()}
                  minDate={new Date(1900, 0, 1)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.dateOfBirth,
                      helperText: errors.dateOfBirth || (formData.dateOfBirth ? `Age: ${calculateAge(formData.dateOfBirth)} years` : ''),
                    },
                  }}
                />
              </LocalizationProvider>

              <Autocomplete
                options={INDIAN_CITIES}
                value={formData.city}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, city: newValue });
                  if (errors.city) {
                    setErrors({ ...errors, city: '' });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    error={!!errors.city}
                    helperText={errors.city}
                  />
                )}
                fullWidth
              />
            </Box>

            <Autocomplete
              multiple
              options={COURSE_OPTIONS}
              value={formData.interested_courses}
              onChange={(event, newValue) => {
                setFormData({ ...formData, interested_courses: newValue });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Interested Courses"
                  placeholder="Select courses..."
                />
              )}
              fullWidth
              freeSolo
              filterSelectedOptions
            />

            {!isEditMode && (
              <TextField
                label="Password *"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password || 'Minimum 6 characters'}
                fullWidth
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
              px: 3,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isEditMode ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default StudentFormDialog;
